import { ForwardedRef, useMemo } from "react";
import { EntityId } from "@reduxjs/toolkit";

import {
    AppBar,
    Toolbar,
    Box,
    Stack,
    IconButton,
    Typography,
    Chip,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloseIcon from "@mui/icons-material/Close";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import MenuIcon from "@mui/icons-material/Menu";

import { useAppDispatch } from "app/hooks";

import { coreActions } from "features/core/store/slice";
import { liveStreamingActions } from "./store/slice";

import { useGetSiteEventsQuery } from "services/streamingApi/siteEvents.api";
import { siteSchedulesApi } from "services/streamingApi/siteSchedules.api";

import { FeatureFlagGuard } from "features/appFeatures/FeatureFlagGuard";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { SiteInformationCard } from "features/siteDetails/SiteInformationCard";
import { SiteStaffingChip } from "./SiteStaffingChip";

import { siteFullname } from "utils/siteFullname";

import { SiteEventType } from "types/SiteEventType";
import { SelectedDeviceProperties } from "./types/SelectedDeviceProperties";
import { Device } from "types/Device";
import Site from "types/Site";

export interface SiteAppBarProps {
    appBarRef: ForwardedRef<HTMLElement>;

    selectedDevices?: {
        deviceId: EntityId;
        properties: SelectedDeviceProperties;
    }[];
    refreshIntervalInMsUnstaffed: number;
    schedulesPollingIntervalInMs: number;
    onCloseSite: (siteId: EntityId) => void;

    site: Site;
    hideNavigationalElements: boolean;
    showSidebar: boolean;
    inWindowMode: boolean;
    inFullscreenMode: boolean;
    fullscreenDevice: Device;
}

export function SiteAppBar(props: SiteAppBarProps) {
    const dispatch = useAppDispatch();

    const { data: siteEventsData } = useGetSiteEventsQuery(
        {
            siteId: props.site.id,
            eventTypes: [
                SiteEventType.Guard,
                SiteEventType.Monitoring,
                SiteEventType.Unplanned_Unstaffed,
                SiteEventType.Information,
            ],
        },
        {
            pollingInterval: props.refreshIntervalInMsUnstaffed,
        },
    );

    const { data: siteSchedule } =
        siteSchedulesApi.useGetSiteScheduleBySiteIdQuery(props.site.id, {
            pollingInterval: props.schedulesPollingIntervalInMs,
        });

    const showAppBarIcon = useMemo(
        () =>
            props.inWindowMode ||
            (props.inFullscreenMode && !props.showSidebar),
        [props.showSidebar, props.inFullscreenMode, props.inWindowMode],
    );

    const toolbarTitle = useMemo(() => {
        const titleParts = [`Club: ${siteFullname(props.site)}`];

        if (props.inFullscreenMode) {
            titleParts.push(`Device: ${props.fullscreenDevice?.name}`);
        }

        return titleParts.join(" - ");
    }, [props.fullscreenDevice?.name, props.inFullscreenMode, props.site]);

    return (
        <AppBar
            ref={props.appBarRef}
            position="relative"
            style={{ scrollMarginTop: 70 }}
            sx={{ marginBottom: props.inWindowMode ? 2 : 0 }}
        >
            <Toolbar variant="dense">
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                    }}
                >
                    <Box
                        sx={{
                            flexGrow: 0,
                            flexShrink: 1,
                            flexBasis: "50%",
                            overflow: "hidden",
                            ml: "-12px",
                            mr: "12px",
                        }}
                    >
                        <Stack
                            sx={{ pr: "12px" }}
                            direction={"row"}
                        >
                            {showAppBarIcon && (
                                <IconButton
                                    edge="start"
                                    color="inherit"
                                    aria-label="menu"
                                    sx={{ mr: 1, ml: 0 }}
                                    onClick={() => {
                                        if (props.inFullscreenMode) {
                                            dispatch(
                                                coreActions.setShowSidebar({
                                                    show: true,
                                                }),
                                            );
                                        } else if (props.inWindowMode) {
                                            dispatch(
                                                liveStreamingActions.setSiteAsActive(
                                                    {
                                                        siteId: props.site.id,
                                                    },
                                                ),
                                            );
                                        }
                                    }}
                                >
                                    {props.inWindowMode ? (
                                        <LocationOnIcon />
                                    ) : (
                                        <MenuIcon />
                                    )}
                                </IconButton>
                            )}
                            <Typography
                                variant="h6"
                                color="inherit"
                                component="span"
                                sx={{
                                    flexShrink: 1,
                                    cursor: "pointer",
                                    alignContent: "center",
                                    display: "inline-block",
                                    "&.MuiTypography-root": {
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    },
                                }}
                                onClick={() =>
                                    dispatch(
                                        liveStreamingActions.setSiteAsActive({
                                            siteId: props.site.id,
                                        }),
                                    )
                                }
                            >
                                {props.site.siteInformation?.siteType && (
                                    <Chip
                                        label={
                                            props.site?.siteInformation
                                                ?.siteType
                                        }
                                        color="tertiary"
                                        size="medium"
                                        sx={{
                                            marginRight: 2,
                                            marginTop: "-3px",
                                            bgColor: "#fc9d03",
                                        }}
                                    />
                                )}
                                {toolbarTitle}
                            </Typography>
                        </Stack>
                    </Box>
                    <SiteStaffingChip
                        staffingTimes={siteSchedule?.staffingTimes}
                    />
                    <Stack
                        sx={{
                            flexGrow: 0,
                            flexShrink: 1,
                            flexBasis: "50%",
                            justifyContent: "flex-end",
                        }}
                        direction={"row"}
                    >
                        <FeatureFlagGuard
                            accessibleWithFeatureFlags={[
                                AppFeature.LiveStreamingSiteDetails,
                            ]}
                        >
                            {props.site && siteEventsData && siteSchedule && (
                                <SiteInformationCard
                                    siteId={props.site.id}
                                    siteSchedule={siteSchedule}
                                    title={toolbarTitle}
                                    siteEvents={siteEventsData}
                                />
                            )}
                        </FeatureFlagGuard>
                        {props.inWindowMode &&
                            !props.hideNavigationalElements && (
                                <IconButton
                                    color="inherit"
                                    onClick={() =>
                                        props.onCloseSite(props.site.id)
                                    }
                                >
                                    <CloseIcon />
                                </IconButton>
                            )}
                        {props.inFullscreenMode && (
                            <IconButton
                                color="inherit"
                                onClick={() =>
                                    dispatch(coreActions.setUIMode("window"))
                                }
                            >
                                <CloseFullscreenIcon />
                            </IconButton>
                        )}
                    </Stack>
                </Box>
            </Toolbar>
        </AppBar>
    );
}
